import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Upload, FileText, Loader2, CheckCircle, XCircle, ArrowLeft, AlertTriangle, Trash2
} from 'lucide-react';
import api from '../services/api';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(amount || 0);

const BulkUploadPage = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
    setResults(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
      f.type === 'application/pdf' || f.type.startsWith('image/')
    );
    if (droppedFiles.length > 0) {
      setFiles(prev => [...prev, ...droppedFiles]);
      setResults(null);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Selecciona al menos un archivo');
      return;
    }

    setUploading(true);
    setProgress(0);
    setResults(null);

    const allResults = [];
    const allErrors = [];

    // Upload one file at a time to avoid proxy size limits
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('files', file);

      try {
        const { data } = await api.post('/invoices/bulk-upload', formData, {
          timeout: 180000
        });
        allResults.push(...(data.results || []));
        allErrors.push(...(data.error_details || []));
      } catch (err) {
        const errMsg = err.response?.data?.detail || err.message || 'Error al procesar';
        allErrors.push({ filename: file.name, error: errMsg, status: 'error' });
      }

      setProgress(Math.round(((i + 1) / files.length) * 100));
    }

    setResults({ results: allResults, errors: allErrors });
    setUploading(false);
    
    if (allResults.length > 0) {
      toast.success(`${allResults.length} facturas importadas correctamente`);
    }
    if (allErrors.length > 0) {
      toast.error(`${allErrors.length} archivos con errores`);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCleanup = async () => {
    if (!window.confirm('¿Eliminar todas las facturas importadas anteriormente? Esta acción no se puede deshacer.')) return;
    setCleaning(true);
    try {
      const { data } = await api.delete('/invoices/bulk-cleanup');
      toast.success(`Eliminadas ${data.deleted_invoices} facturas y ${data.deleted_clients} clientes importados`);
      setResults(null);
      setFiles([]);
    } catch (err) {
      toast.error('Error al limpiar: ' + (err.response?.data?.detail || err.message));
    }
    setCleaning(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/invoices')} className="p-2 hover:bg-stone-100 rounded-xl" data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight" data-testid="bulk-upload-title">Importar Facturas</h1>
          <p className="text-stone-500 text-sm">Sube tus facturas anteriores y la IA las organizará</p>
        </div>
        <div className="ml-auto">
          <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 rounded-xl" onClick={handleCleanup} disabled={cleaning} data-testid="bulk-cleanup-button">
            {cleaning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {cleaning ? 'Limpiando...' : 'Limpiar importaciones'}
          </Button>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">La IA analizará cada factura</p>
          <p>Sube archivos PDF o imágenes (JPG, PNG). La IA extraerá automáticamente: número de factura, cliente, fecha, importes e IVA. Se organizarán por trimestres en el historial.</p>
        </div>
      </div>

      {/* Upload Zone */}
      {!results && (
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />

          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 sm:p-12 text-center cursor-pointer transition-all duration-200 ${
              isDragging 
                ? 'border-[#FF6B4A] bg-[#FF6B4A]/5 scale-[1.01]' 
                : 'border-stone-300 hover:border-[#FF6B4A] hover:bg-orange-50'
            }`}
            data-testid="drop-zone"
          >
            <Upload className={`w-10 h-10 mx-auto mb-4 transition-colors ${isDragging ? 'text-[#FF6B4A]' : 'text-stone-400'}`} />
            <p className="text-lg font-medium text-stone-700 mb-1">
              {isDragging ? 'Suelta los archivos aquí' : 'Arrastra o selecciona facturas'}
            </p>
            <p className="text-sm text-stone-500">PDF, JPG, PNG · Sin límite de archivos</p>
            {files.length > 0 && (
              <p className="text-xs text-[#FF6B4A] mt-2 font-medium">
                Puedes seguir añadiendo más archivos
              </p>
            )}
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-stone-700">{files.length} archivo{files.length !== 1 ? 's' : ''} seleccionado{files.length !== 1 ? 's' : ''}</p>
                <Button variant="ghost" size="sm" onClick={() => setFiles([])} className="text-xs text-stone-500">Limpiar</Button>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-stone-50 rounded-xl">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-stone-400 flex-shrink-0" />
                      <span className="text-sm truncate">{file.name}</span>
                      <span className="text-xs text-stone-400 flex-shrink-0">{(file.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <button onClick={() => removeFile(i)} className="p-1 hover:bg-stone-200 rounded-xl flex-shrink-0">
                      <XCircle className="w-3.5 h-3.5 text-stone-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {files.length > 0 && (
            <div className="mt-4">
              {uploading ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-[#FF6B4A]" />
                    <span className="text-sm font-medium">Procesando facturas con IA... {progress}%</span>
                  </div>
                  <div className="w-full bg-stone-200 rounded-full h-2">
                    <div className="bg-[#FF6B4A] h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
                  </div>
                  <p className="text-xs text-stone-500">Esto puede tardar unos segundos por factura. No cierres la página.</p>
                </div>
              ) : (
                <Button onClick={handleUpload} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto" data-testid="start-upload-button">
                  <Upload className="w-4 h-4 mr-2" />
                  Importar {files.length} factura{files.length !== 1 ? 's' : ''}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-testid="success-count">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-bold text-green-800">{results.results.length}</span>
              </div>
              <p className="text-sm text-green-700">Facturas importadas</p>
            </div>
            {results.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="error-count">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <span className="font-bold text-red-800">{results.errors.length}</span>
                </div>
                <p className="text-sm text-red-700">Con errores</p>
              </div>
            )}
          </div>

          {/* Imported invoices */}
          {results.results.length > 0 && (
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100">
              <div className="px-4 sm:px-6 py-3 border-b border-stone-100">
                <h3 className="font-medium">Facturas Importadas</h3>
              </div>
              <div className="divide-y divide-zinc-100">
                {results.results.map((r, i) => (
                  <div key={i} className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium">{r.invoice_number}</p>
                        <p className="text-xs text-stone-500">{r.client_name} · {r.date}</p>
                      </div>
                    </div>
                    <span className="font-mono text-sm font-medium">{formatCurrency(r.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Errors */}
          {results.errors.length > 0 && (
            <div className="bg-white border border-red-200 rounded-xl">
              <div className="px-4 sm:px-6 py-3 border-b border-red-200">
                <h3 className="font-medium text-red-700">Archivos con errores</h3>
              </div>
              <div className="divide-y divide-zinc-100">
                {results.errors.map((e, i) => (
                  <div key={i} className="px-4 sm:px-6 py-3 flex items-center gap-3">
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{e.filename}</p>
                      <p className="text-xs text-red-500">{e.error}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setResults(null); setFiles([]); }} className="rounded-xl" data-testid="upload-more-button">
              Subir más facturas
            </Button>
            <Button onClick={() => navigate('/invoices')} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="go-to-invoices-button">
              Ver Facturas
            </Button>
            <Button variant="outline" onClick={() => navigate('/history')} className="rounded-xl">
              Ver Historial
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUploadPage;
